import { FC, useContext, useMemo, useState } from 'react';

import {
  AddUserIcon,
  AlienIcon,
  H1,
  H2,
  InfoBlock,
  OtherWorkerIcon,
  Preloader,
  ReferralIcon,
  Tooltip as TooltipKit,
  XReportIcon,
} from '@smartshell/ui-kit';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ReportSummaryBlock, ReportSummaryTooltip } from 'components';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { formatMoney, useSummaryValues } from '../utils';
import s from './Clients.module.scss';
import { IClientsProps } from './interfaces';

export const Clients: FC<IClientsProps> = ({
  data,
  labels,
  values,
  loading,
  sum,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const size = useWindowResize();

  const { getValue } = useSummaryValues(data, loading);

  const normalizedData = [
    {
      text: t('visitors.unique.client'),
      value: `${getValue('unique_clients')} ${t('contracted.persons')}`,
      icon: <OtherWorkerIcon color="secondary" />,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.unique.client')}
          footer={t('unique.client.tooltip')}
          variant="secondary"
        />
      ),
    },
    {
      text: t('visitors.new.client'),
      value: `${getValue('new_clients_count')} ${t('contracted.persons')}`,
      icon: <AddUserIcon color="secondary" />,
      percent: `${getValue('new_clients_percent', true)}%`,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.new.client')}
          footer={t('unique.new.client.tooltip')}
          variant="secondary"
        />
      ),
    },
    {
      text: t('visitors.return.client'),
      value: `${getValue('repeat_visits')} ${t('contracted.persons')}`,
      icon: <ReferralIcon color="secondary" />,
      percent: `${getValue('repeat_visits_percent', true)}%`,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.return.client')}
          footer={t('unique.return.client.tooltip')}
          variant="secondary"
        />
      ),
    },
    {
      text: t('visitors.guest.seance'),
      value: `${getValue('guest_sessions')} ${t('pcs')}`,
      percent: `${getValue('guest_sessions_percent', true)}%`,
      icon: <AlienIcon color="secondary" />,
    },
    {
      text: t('visitors.client.middle.check'),
      value: `${formatMoney(getValue('client_apru'), currency.sign)}`,
      icon: <XReportIcon color="secondary" />,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.client.middle.check.title')}
          footer={t('visitors.client.middle.check.text')}
          variant="secondary"
        />
      ),
    },
  ];

  const chartData = useMemo(() => {
    if (!labels || !values) return [];

    return values.values.map((value, index) => ({
      name: labels[index],
      value: value || 0,
    }));
  }, [labels, values]);

  const [focusBar, setFocusBar] = useState<number | null>(null);

  return (
    <div className={s.container}>
      <H1>{t('clients.total')}</H1>
      <div className={s.summary_block}>
        {normalizedData.map((item) => (
          <ReportSummaryBlock key={item.text} item={item} />
        ))}
      </div>
      {size.width > 960 ? (
        <div className={s.content}>
          <div className={s.header}>
            <H2 className={s.title}>{t('visitors.clients.by.visit')}</H2>
            <TooltipKit
              contentClassName={s.tooltip}
              content={t('review.clients.tooltip')}
            />
          </div>

          {(labels === undefined || values?.values.every((val) => val === 0)) &&
          !loading ? (
            <div className={s.placeholder}>
              {values?.values.every((val) => val === 0)
                ? t('visitors.empty.data')
                : t('report.placeholder.emptyDates')}
            </div>
          ) : (
            <Preloader loading={loading}>
              <ResponsiveContainer
                className={s.data}
                width="100%"
                height="100%"
              >
                <BarChart
                  margin={{ top: 25 }}
                  data={chartData}
                  onMouseMove={(state) => {
                    if (state.isTooltipActive) {
                      setFocusBar(state.activeTooltipIndex ?? null);
                    } else {
                      setFocusBar(null);
                    }
                  }}
                >
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    dy={8}
                    tick={{ fill: 'var(--text-primary)' }}
                    fontSize={12}
                  />
                  <YAxis
                    domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                    tickFormatter={(value) => `${value}`}
                    axisLine={false}
                    tickLine={false}
                    dx={-23}
                    tick={{
                      fill: 'var(--text-primary)',
                      style: { whiteSpace: 'nowrap' },
                    }}
                    fontSize={12}
                  />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-item-secondary)' }}
                    content={() => null}
                  />

                  <Bar
                    fill="var(--bg-blue-10)"
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="value"
                      content={(props) => {
                        const { x, y, width, value } = props;
                        const percent =
                          typeof sum === 'number' && sum > 0
                            ? `${((Number(value) / sum) * 100).toFixed(0)}%`
                            : '';
                        const XPosition = Number(x) + Number(width) / 2;
                        const YPosition = Number(y) - 8;

                        return (
                          <text
                            x={XPosition}
                            y={YPosition}
                            fill="var(--text-primary)"
                            fontSize="18"
                            textAnchor="middle"
                          >
                            {percent}
                          </text>
                        );
                      }}
                    />
                    {chartData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          focusBar === index
                            ? 'var(--bg-blue)'
                            : 'var(--bg-blue-10)'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Preloader>
          )}
        </div>
      ) : (
        <InfoBlock
          className={s.mobile_info}
          title={t('mobile.placeholder')}
          iconName="ComputerIcon"
          color="blue"
        />
      )}
    </div>
  );
};
